import { Size } from '@ps/web-core-enums';
import { StarsButton as Button, Translation, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Arrow from '@swf/core/assets/icons/arrow.svg';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { InstantBonus } from '@swf/core/types/InstantBonus';
import { Category } from '@swf/core/types/StarsCoinReward';

import Container from '../Container/Container';
import InstantBonusTile from '../InstantBonusTile/InstantBonusTile';
import RefreshButton from '../RefreshButton/RefreshButton';
import type WithClose from '../WithClose';

import { TXTMOB_See_All } from './InstantBonuses.messages.json';
import styles from './InstantBonuses.scss';

interface Props extends WithClose {
  ib: InstantBonus[];
  onReload(): void;
  refreshTimestamp: number;
}
const InstantBonuses: React.FC<Props> = ({ close, ib, refreshTimestamp, onReload }) => {
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const bigScreen = greaterThan(Size.Large);
  const maxIB = isMobile || !bigScreen ? 1 : 2;
  const ibList = ib.slice(0, maxIB);
  const { modal } = useDispatch<Dispatch>();

  const onIBClick = useCallback(
    (ib?: InstantBonus) => {
      const id = ib ? ib.bonusId : '';
      if (isEmbedded) {
        window.starsweb?.app.execute('awardstatus/InstantBonuses');
      } else {
        modal.enqueue({
          path: `/account/my-stars/bonus/instant-bonuses/${id}`
        });
      }
    },
    [modal, isEmbedded]
  );

  return (
    <Container
      category={Category.IB}
      close={close}
      right={
        <RefreshButton
          className={styles.refresh}
          onClick={() => onReload()}
          refreshTimestamp={refreshTimestamp}
        />
      }
    >
      <div
        className={classNames(styles.ibList, {
          [styles.compact]: ibList.length > 1,
          [styles.seeAll]: ib.length > maxIB
        })}
      >
        {ibList.map((value, idx) => (
          <InstantBonusTile
            key={value.bonusId}
            ib={value}
            compact={ibList.length > 1}
            onClick={() => onIBClick(value)}
          />
        ))}
        {ib.length > maxIB && (
          <div className={styles.seeAllBlock}>
            <Button
              className={classNames(styles.button, styles.right)}
              onClick={() => onIBClick()}
              variant="casino"
              size="large"
              icon={<Arrow />}
            />
            <Translation {...TXTMOB_See_All} tagName="span" />
          </div>
        )}
      </div>
    </Container>
  );
};

export default InstantBonuses;
